import { Box } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import {
  useGetAiModels,
  useGetListChat,
  useGetMessageInChat,
} from '../../hooks/useChatStream';
import { useToggleStore } from '../../hooks/useTogger';
import { useWindowHeight } from '../../hooks/useWindowHeight';
import { useMe } from '../../hooks/useAuthencation';
import { IMessageInChat, IAiModel, IListChat } from '../../api/chat';
import { useNavigate } from 'react-router-dom';

// Import our components
import ChatHeader from './ChatHeader';
import MenuBar from './MenuBar';
import EmptyChat from './EmptyChat';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import ModelSelectDialog from './ModelSelectDialog';

// Import chat logic
import { useChatLogic } from './chatLogic';
const LIMIT = 30;

function ChatScreen() {
  const navigate = useNavigate();
  const { data: profile } = useMe();
  const { data: aiModels } = useGetAiModels();

  const { width, toggle } = useToggleStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const windowHeight = useWindowHeight();
  const [conversationIdCurrent, setConversationIdCurrent] = useState<
    string | null
  >(null);
  const [aiModelCurrent, setAiModelCurrent] = useState<IAiModel | null>(null);

  const [offsetListChat, setOffsetListChat] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const { data: dataListChat, isLoading: isLoadingListChat } = useGetListChat({
    limit: LIMIT,
    offset: offsetListChat,
    search: searchQuery,
  });
  const [listChatPage, setListChatPage] = useState<IListChat[]>([]);

  const handleUpdateSearchQuery = (query: string) => {
    setListChatPage([]);
    setSearchQuery(query);
    setOffsetListChat(0);
  };

  const [offset] = useState(0);

  const { data: messageInChat, isLoading: isLoadingMessageInChat } =
    useGetMessageInChat(conversationIdCurrent, {
      limit: 10,
      offset: offset,
    });

  const [items, setItems] = useState<IMessageInChat[]>([]);

  const effectRan = useRef(false);
  const [openModelDialog, setOpenModelDialog] = useState(false);

  // Use our chat logic hook
  const {
    handleSendMessage,
    handleSendMessageWithFile,
    handleChooseConversation: handleChooseConversationLogic,
    newConversation: newConversationLogic,
    isAiLoading,
    idConversationNew,
    handleStopConversation,
  } = useChatLogic();

  useEffect(() => {
    if (effectRan.current === false) {
      const conversationId = new URLSearchParams(window.location.search).get(
        'conversationId'
      );
      if (conversationId) {
        setConversationIdCurrent(conversationId);
      }

      return () => {
        effectRan.current = true;
      };
    }
  }, [navigate]);

  useEffect(() => {
    const itemsReverse = [...(messageInChat?.items || [])];
    setItems(itemsReverse.reverse());
  }, [messageInChat]);

  useEffect(() => {
    if (conversationIdCurrent && aiModels && listChatPage) {
      const conversation = listChatPage.find(
        (chat) => chat.id.toString() === conversationIdCurrent
      );
      if (conversation) {
        setAiModelCurrent(
          aiModels?.find(
            (model) => model.id.toString() === conversation.aiModelId.toString()
          ) || null
        );
      }
    }
  }, [conversationIdCurrent, listChatPage, aiModels]);

  // Wrap the handleSendMessage from chatLogic with current state
  const sendMessage = async (
    newMessage: string,
    file?: File,
    fileUrl?: string
  ) => {
    if (file) {
      await handleSendMessageWithFile(
        newMessage,
        file,
        fileUrl || '',
        items,
        setItems,
        setListChatPage,
        conversationIdCurrent,
        aiModelCurrent,
        setAiModelCurrent,
        aiModels,
        listChatPage,
        profile?.id
      );
    } else {
      await handleSendMessage(
        newMessage,
        items,
        setItems,
        setListChatPage,
        conversationIdCurrent,
        aiModelCurrent,
        setAiModelCurrent,
        aiModels,
        listChatPage,
        profile?.id
      );
    }
  };

  // Wrap the handleChooseConversation from chatLogic with current state
  const handleChooseConversation = (conversation: IListChat) => {
    handleChooseConversationLogic(
      conversation,
      aiModels,
      setConversationIdCurrent,
      setAiModelCurrent
    );
  };

  // Wrap the newConversation from chatLogic with current state
  const newConversation = () => {
    newConversationLogic(setConversationIdCurrent, setItems);
  };

  useEffect(() => {
    if (dataListChat !== undefined) {
      setListChatPage((prev) => [...prev, ...dataListChat]);
    }
  }, [dataListChat]);

  const fetchNextPage = () => {
    setOffsetListChat(listChatPage.length);
  };

  const handleOpenModelDialog = () => {
    setOpenModelDialog(true);
  };

  const handleCloseModelDialog = () => {
    setOpenModelDialog(false);
  };

  const handleSelectModel = (model: IAiModel) => {
    setAiModelCurrent(model);
    handleCloseModelDialog();
  };

  return (
    <div className="flex">
      <MenuBar
        hasMore={(dataListChat?.length || 0) === LIMIT}
        isLoading={isLoadingListChat}
        data={listChatPage}
        fetchNextPage={fetchNextPage}
        profile={profile}
        handleChooseConversation={handleChooseConversation}
        newConversation={newConversation}
        idConversationCurrent={conversationIdCurrent || idConversationNew}
        handleUpdateSearchQuery={handleUpdateSearchQuery}
        searchQuery={searchQuery}
      />
      <div
        className={`flex items-center justify-center md:w-[unset] w-full`}
        style={{
          height: `${windowHeight}px`,
        }}
      >
        <Box
          style={{
            width: `calc(100vw - ${width}px)`,
            transition: 'width 0.3s ease-in-out',
          }}
          sx={{
            height: {
              xs: `${windowHeight}px`,
              md: '100vh',
            },
            backgroundColor: 'white',
            boxShadow: 3,
            display: 'flex',
            flexDirection: 'column',
            '@media (max-width: 768px)': {
              width: '100% !important',
            },
          }}
        >
          <ChatHeader width={width} toggle={toggle} profile={profile} />

          <div
            className="flex-1 mb-2 sm:mb-4 overflow-auto p-2 sm:p-4 max-w-[600px] mx-auto w-full"
            id="scrollableDiv"
          >
            {items.length === 0 ? (
              <EmptyChat
                aiModelCurrent={aiModelCurrent}
                defaultModel={aiModels?.[0]}
                handleOpenModelDialog={handleOpenModelDialog}
              />
            ) : (
              <MessageList
                items={items}
                messagesEndRef={messagesEndRef}
                isLoading={isLoadingMessageInChat && !!conversationIdCurrent}
                aiModelCurrent={aiModelCurrent || aiModels?.[0]}
              />
            )}
          </div>

          <div className="flex justify-center px-2 md:px-0">
            <ChatInput
              onSendMessage={sendMessage}
              isLoading={isAiLoading}
              currentModel={aiModelCurrent || aiModels?.[0]}
              profile={profile}
              aiLoading={isAiLoading}
              handleStopConversation={() =>
                handleStopConversation(
                  conversationIdCurrent || idConversationNew
                )
              }
            />
          </div>
        </Box>
      </div>

      <ModelSelectDialog
        open={openModelDialog}
        onClose={handleCloseModelDialog}
        aiModels={aiModels}
        onSelectModel={handleSelectModel}
      />
    </div>
  );
}

export default ChatScreen;
